'use client'

import type { ReactNode } from 'react'
import * as React from 'react'
import { flexRender, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table'
import {
  ChevronLeft,
  ChevronRight,
  // Download
} from 'lucide-react'

import type { RoyaltyRunningBalanceEntry } from '@package/api'
import type { ServerTableData } from '@helper/utils/types'
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TableBody,
  TableCell,
  Table as TableComponent,
  TableHead,
  TableHeader,
  TableRow,
} from '@ui/web/components'
import { formatCurrency, formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { RoyaltyRunningBalancePageType } from '../index'
import { getRoyaltyRunningBalanceColumns, royaltyRunningBalanceColumns } from './columns'

interface RoyaltyRunningBalanceTableProps {
  type?: RoyaltyRunningBalancePageType
  data: RoyaltyRunningBalanceEntry[]
  isLoading?: boolean
  className?: string
  /** Custom render function for the Report button link. Receives royaltyId and should return a React node. */
  renderReportLink?: (royaltyId: string) => ReactNode
  /** When type is admin, called for PENDING withdrawals to open Pay withdrawal dialog. */
  onPayWithdrawal?: (entry: { withdrawalId: string; amount: number; currency: string }) => void
  /** When provided, table uses server-side pagination (total/page from server, prev/next trigger callbacks). */
  pagination?: ServerTableData<RoyaltyRunningBalanceEntry>['pagination']
  onPageChange?: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
}

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const

export function RoyaltyRunningBalanceTable({
  type,
  data,
  isLoading,
  className,
  renderReportLink,
  onPayWithdrawal,
  pagination,
  onPageChange,
  onPageSizeChange,
}: RoyaltyRunningBalanceTableProps) {
  const columns = React.useMemo(
    () =>
      getRoyaltyRunningBalanceColumns({
        type,
        renderReportLink,
        onPayWithdrawal,
      }),
    [type, renderReportLink, onPayWithdrawal],
  )

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(pagination
      ? { manualPagination: true, pageCount: pagination.pageCount }
      : { getPaginationRowModel: getPaginationRowModel(), initialState: { pagination: { pageSize: 10 } } }),
    getRowId: (row) => row.id,
  })

  const totalCount = pagination?.total ?? data.length
  const pageCount = pagination?.pageCount ?? table.getPageCount()
  const currentPage = pagination?.page ?? table.getState().pagination.pageIndex + 1
  const serverMode = Boolean(pagination && onPageChange)

  const _handleDownloadCSV = React.useCallback(() => {
    const rows = table.getRowModel().rows.map((r) => r.original)
    if (rows.length === 0) return
    const escapeCSVField = (field: string): string => {
      if (field.includes(',') || field.includes('"') || field.includes('\n')) {
        return `"${field.replace(/"/g, '""')}"`
      }
      return field
    }
    const currency = rows[0]?.currency ?? 'IDR'
    try {
      const headers = ['Date', 'Description', 'Amount', 'Type', 'Status']
      const csvRows = rows.map((entry) => {
        const date = formatDate(entry.date, { dateStyle: 'medium' })
        const amount = formatCurrency(Math.abs(entry.amount), { currency, maximumFractionDigits: 2 })
        return [
          escapeCSVField(date),
          escapeCSVField(entry.description),
          escapeCSVField(amount),
          escapeCSVField(entry.type),
          escapeCSVField(entry.withdrawalStatus ?? ''),
        ].join(',')
      })
      const csvContent = [headers.join(','), ...csvRows].join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `royalty-balance-${new Date().toISOString().split('T')[0]}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('[RoyaltyRunningBalanceTable] Download CSV error:', error)
    }
  }, [table])

  if (isLoading) {
    return (
      <div className={cn('srd:border-neutrals-n-20 srd:overflow-hidden srd:rounded-md srd:border', className)}>
        <TableComponent>
          <TableHeader className='srd:bg-neutrals-nvar-10'>
            <TableRow>
              {columns.map((col, index) => (
                <TableHead key={col.id ?? `col-${index}`} className='srd:text-nowrap'>
                  {typeof col.header === 'function' ? col.header({} as never) : col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                {royaltyRunningBalanceColumns.map((_, j) => (
                  <TableCell key={j}>
                    <div className='srd:bg-neutrals-n-20 srd:h-6 srd:animate-pulse srd:rounded' />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </TableComponent>
      </div>
    )
  }

  return (
    <section className={cn('srd:w-full srd:space-y-4', className)}>
      <div className='srd:flex srd:flex-wrap srd:items-center srd:justify-between srd:gap-4'>
        {/* <Button variant='outline' onClick={handleDownloadCSV} disabled={table.getRowModel().rows.length === 0}>
          <Download className='srd:mr-2 srd:size-4' />
          Download balance
        </Button> */}
        <div className='srd:flex srd:flex-wrap srd:items-center srd:justify-end srd:gap-2'>
          <span className='srd:text-neutrals-n-75 srd:text-sm'>
            Total: <span className='srd:text-foreground srd:font-medium'>{totalCount}</span>
          </span>
          <Select
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            value={String(serverMode ? pagination!.pageSize : table.getState().pagination.pageSize)}
            onValueChange={(v) => {
              const n = Number(v)
              if (Number.isNaN(n)) return
              if (serverMode && onPageSizeChange) {
                onPageSizeChange(n)
                onPageChange?.(1)
              } else {
                table.setPageSize(n)
              }
            }}
          >
            <SelectTrigger className='srd:h-8 srd:w-[72px]'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className='srd:text-neutrals-n-75 srd:flex srd:items-center srd:gap-1 srd:text-sm'>
            <Button
              variant='outline'
              size='icon'
              className='srd:h-8 srd:w-8'
              onClick={() => (serverMode ? onPageChange?.(currentPage - 1) : table.previousPage())}
              disabled={serverMode ? currentPage <= 1 : !table.getCanPreviousPage()}
            >
              <ChevronLeft className='srd:h-4 srd:w-4' />
            </Button>
            <span className='srd:min-w-16 srd:text-center'>
              {currentPage} / {pageCount || 1}
            </span>
            <Button
              variant='outline'
              size='icon'
              className='srd:h-8 srd:w-8'
              onClick={() => (serverMode ? onPageChange?.(currentPage + 1) : table.nextPage())}
              disabled={serverMode ? currentPage >= pageCount : !table.getCanNextPage()}
            >
              <ChevronRight className='srd:h-4 srd:w-4' />
            </Button>
          </div>
        </div>
      </div>

      <div className='srd:border-neutrals-n-20 srd:overflow-hidden srd:rounded-md srd:border'>
        <TableComponent>
          <TableHeader className='srd:bg-neutrals-nvar-10'>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className='srd:text-nowrap'>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className='srd:text-neutrals-n-75 srd:h-24 srd:text-center'>
                  No transactions found
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </TableComponent>
      </div>
    </section>
  )
}
